// redux/slices/takbullPaymentGatewaySlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getServerUrl } from "../../Utils/serverSelector";
import { getJwtToken } from "../../Utils/authStorage";
import { resetStore } from "./resetStore";

/* ===================== HELPERS ===================== */

const axiosRequest = async ({
    url,
    method = "GET",
    data = null,
    params = null,
}) => {
    const jwt = await getJwtToken();

    const m = String(method).toUpperCase();
    const hasBody = data != null && !["GET", "DELETE"].includes(m);

    const response = await axios({
        url: `${getServerUrl()}${url}`,
        method,
        headers: {
            ...(hasBody ? { "Content-Type": "application/json" } : {}),
            ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        ...(hasBody ? { data } : {}),
        params,
        timeout: 15000,
    });

    return response.data;
};

const getError = (error, fallback) =>
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallback;

const pickConnected = (payload) => {
    const raw =
        payload?.connected ??
        payload?.data?.connected ??
        payload?.status?.connected ??
        payload?.result?.connected ??
        payload?.defaults?.connected;

    if (typeof raw === "boolean") return raw;
    if (typeof raw === "number") return raw === 1;

    if (typeof raw === "string") {
        const value = raw.trim().toLowerCase();
        return value === "true" || value === "1" || value === "yes";
    }

    return false;
};

/* ===================== THUNKS ===================== */

export const saveTakbullPaymentCreds = createAsyncThunk(
    "takbullPaymentGateway/save",
    async (
        { uid, apiKey, apiSecret, defaults, vatRate, vatCode, paymentsEnabled, maxPayments },
        { rejectWithValue }
    ) => {
        try {
            await axiosRequest({
                url: "/payments/takbull/credentials",
                method: "POST",
                data: {
                    uid,
                    apiKey,
                    apiSecret,
                    defaults,
                    vatRate,
                    vatCode,
                    ...(paymentsEnabled !== undefined ? { paymentsEnabled } : {}),
                    ...(maxPayments !== undefined ? { maxPayments } : {}),
                },
            });
            
            const data = await axiosRequest({
                url: `/payments/takbull/credentials/${uid}`,
            });

            return data;
        } catch (error) {
            return rejectWithValue(
                getError(error, "Failed to save Takbull credentials")
            );
        }
    }
);

export const fetchTakbullPaymentStatus = createAsyncThunk(
    "takbullPaymentGateway/status",
    async (uid, { rejectWithValue }) => {
        try {
            const data = await axiosRequest({
                url: `/payments/takbull/credentials/${uid}`,
            });

            return data;
        } catch (error) {
            return rejectWithValue(
                getError(error, "Failed to fetch Takbull status")
            );
        }
    }
);

export const disconnectTakbullPayment = createAsyncThunk(
    "takbullPaymentGateway/disconnect",
    async (uid, { rejectWithValue }) => {
        try {
            await axiosRequest({
                url: `/payments/takbull/credentials/${uid}`,
                method: "DELETE",
            });

            return true;
        } catch (error) {
            return rejectWithValue(
                getError(error, "Failed to disconnect Takbull")
            );
        }
    }
);

/* ===================== STATE ===================== */

const initialState = {
    connected: false,
    keyMask: null,
    updatedAt: null,
    defaults: null,

    status: "idle",
    saving: false,
    error: null,
};

/* ===================== SLICE ===================== */

const takbullPaymentGatewaySlice = createSlice({
    name: "takbullPaymentGateway",

    initialState,

    reducers: {
        clearTakbullPaymentGatewayError(state) {
            state.error = null;
        },

        resetTakbullPaymentGatewayStatus(state) {
            state.status = "idle";
            state.saving = false;
            state.error = null;
        },
    },

    extraReducers: (builder) => {
        builder

            .addCase(saveTakbullPaymentCreds.pending, (state) => {
                state.saving = true;
                state.error = null;
            })

            .addCase(saveTakbullPaymentCreds.fulfilled, (state, action) => {
                state.saving = false;

                state.connected = pickConnected(action.payload);
                state.keyMask = action.payload?.keyMask || null;
                state.updatedAt = action.payload?.updatedAt || null;
                state.defaults = action.payload?.defaults || null;
            })

            .addCase(saveTakbullPaymentCreds.rejected, (state, action) => {
                state.saving = false;
                state.error =
                    action.payload ||
                    action.error?.message ||
                    "Save failed";
            })

            .addCase(fetchTakbullPaymentStatus.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })

            .addCase(fetchTakbullPaymentStatus.fulfilled, (state, action) => {
                state.status = "succeeded";

                state.connected = pickConnected(action.payload);
                state.keyMask = action.payload?.keyMask || null;
                state.updatedAt = action.payload?.updatedAt || null;
                state.defaults = action.payload?.defaults || null;
            })

            .addCase(fetchTakbullPaymentStatus.rejected, (state, action) => {
                state.status = "failed";
                state.error =
                    action.payload ||
                    action.error?.message ||
                    "Status failed";
            })

            .addCase(disconnectTakbullPayment.pending, (state) => {
                state.saving = true;
                state.error = null;
            })

            .addCase(disconnectTakbullPayment.fulfilled, (state) => {
                state.saving = false;

                state.connected = false;
                state.keyMask = null;
                state.updatedAt = null;
                state.defaults = null;
            })

            .addCase(disconnectTakbullPayment.rejected, (state, action) => {
                state.saving = false;
                state.error =
                    action.payload ||
                    action.error?.message ||
                    "Disconnect failed";
            })

            .addCase(resetStore, () => initialState);
    },
});

/* ===================== ACTIONS ===================== */

export const {
    clearTakbullPaymentGatewayError,
    resetTakbullPaymentGatewayStatus,
} = takbullPaymentGatewaySlice.actions;

/* ===================== SELECTORS ===================== */

export const selectTakbullPaymentGatewayState = (state) =>
    state.takbullPaymentGateway;

export const selectTakbullPaymentGateway = (state) =>
    state.takbullPaymentGateway ?? initialState;

export const selectTakbullConnected = (state) =>
    !!state.takbullPaymentGateway?.connected;

export const selectTakbullPaymentGatewayStatus = (state) =>
    state.takbullPaymentGateway?.status || "idle";

export const selectTakbullPaymentGatewaySaving = (state) =>
    !!state.takbullPaymentGateway?.saving;

export const selectTakbullPaymentGatewayError = (state) =>
    state.takbullPaymentGateway?.error || null;

export default takbullPaymentGatewaySlice.reducer;